// useScope / useRole / RoleGate — fine-grained UI gating for LIFF pages.
//
// Usage:
//   const canWrite = useScope("write:incident");
//   <button disabled={!canWrite} title={canWrite ? "" : "您的角色無此權限"}>提交</button>
//
//   const role = useRole();
//   if (role === "auditor") return <ReadOnlyBadge />;
//
// Pages should mount inside <RoleGate allow={["officer","tenant_admin"]}>...</RoleGate>
// to redirect/block unauthorized roles at the route level (server already
// enforces, but UX is much better if we avoid the 403 flash).

import React, { useEffect } from "react";
import { useLiff } from "./useLiff.js";

// react-router-dom is a peer of the frontend bundle; we keep the import
// optional via a try/catch dynamic resolve because RoleGate may render in
// the dashboard tree as well, where react-router is also present, but
// we want to fail soft if some shell strips it out.
import { Navigate } from "react-router-dom";

/**
 * True iff the current LIFF session has ALL of `required` scopes (or
 * `admin:tenant` wildcard). Returns false during init / when no session.
 */
export function useScope(...required: string[]): boolean {
  const { session } = useLiff();
  if (!session) return false;
  const have = new Set(session.scopes ?? []);
  if (have.has("admin:tenant")) return true;
  for (const r of required) if (!have.has(r)) return false;
  return true;
}

/** True iff ANY of `required` is present (or wildcard). */
export function useAnyScope(...required: string[]): boolean {
  const { session } = useLiff();
  if (!session) return false;
  const have = new Set(session.scopes ?? []);
  if (have.has("admin:tenant")) return true;
  for (const r of required) if (have.has(r)) return true;
  return false;
}

export function useRole(): string | null {
  const { session } = useLiff();
  return session?.role ?? null;
}

export function useTenantId(): string | null {
  const { session } = useLiff();
  return session?.tenant_id ?? null;
}

/**
 * Page-level role gate. Renders fallback (or RoleForbidden) when the
 * session's role isn't on the allow-list. Use at the top of each LIFF page.
 */
export interface RoleGateProps {
  allow: readonly string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function RoleGate({ allow, children, fallback }: RoleGateProps): React.ReactElement {
  const { session, isReady } = useLiff();

  // Two distinct failure modes — handle them independently:
  //
  // (1) No session yet: the user isn't bound to any tenant, OR the
  //     LIFF SDK couldn't mint one. The right UX is to push them to
  //     /liff/binding so they can redeem an invite code, NOT to show a
  //     terminal "forbidden" page (which is a dead-end). The /liff/binding
  //     route deliberately doesn't apply RoleGate to itself, so the
  //     redirect can't loop. If this RoleGate is currently rendering on
  //     the binding page (no-op redirect), <Navigate> short-circuits.
  //
  // (2) Session exists but role isn't on the allow list: this is a
  //     genuine permission error — show RoleForbidden so the user knows
  //     which role they have vs. which the page needs. Don't redirect
  //     because the next page over would 403 the same way.
  if (!isReady) return <></>;
  if (!session) {
    if (typeof window !== "undefined" && window.location.pathname === "/liff/binding") {
      // Already on the binding page — render whatever the caller passed
      // as fallback (or render nothing so the binding flow can mount).
      return <>{fallback ?? null}</>;
    }
    return <Navigate to="/liff/binding" replace />;
  }
  if (!allow.includes(session.role)) {
    return <>{fallback ?? <RoleForbidden role={session.role} reason="您的身分目前無法使用此功能" allow={allow} />}</>;
  }
  return <>{children}</>;
}

// Disable the unused-import warning for `useEffect` until we add a
// lifecycle hook here (left in scope so future fix can attach a metric
// emit when redirect happens).
void useEffect;

interface RoleForbiddenProps {
  role: string | null;
  reason: string;
  allow?: readonly string[];
}

const ROLE_LABEL: Record<string, string> = {
  tenant_admin:      "管理員",
  manager:           "案場主管",
  officer:           "巡邏員",
  incident_reporter: "通報員",
  auditor:           "稽核員",
  super_admin:       "平台管理員",
  guest:             "尚未綁定",
};
function roleLabel(r: string | null): string {
  if (!r) return "尚未登入";
  return ROLE_LABEL[r] ?? r;
}

export function RoleForbidden({ role, reason, allow }: RoleForbiddenProps): React.ReactElement {
  return (
    <div className="min-h-[80dvh] bg-bg-base text-text-primary flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl bg-bg-surface border border-border p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-2 h-2 rounded-full bg-warning" aria-hidden />
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">無權限</span>
        </div>
        <p className="text-sm leading-relaxed text-text-primary">此功能您目前無法使用。</p>
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px] text-text-secondary">
          <dt className="text-text-muted">您的身分</dt><dd>{roleLabel(role)}</dd>
          <dt className="text-text-muted">說明</dt><dd>{reason}</dd>
        </dl>
        <p className="mt-4 text-xs text-text-muted">若您認為這是錯誤,請聯絡您的單位管理員。</p>
      </div>
    </div>
  );
}

/**
 * Wrapper for a single button / interactive element. Renders the disabled
 * variant + tooltip when scope missing.
 *
 * Usage:
 *   <ScopeGate need="write:incident" disabled={someBusyState}>
 *     <button>提交</button>
 *   </ScopeGate>
 *
 * Children should accept disabled / aria-disabled. We clone the element
 * and set those props + a title.
 */
export interface ScopeGateProps {
  need: string | string[];
  any?: boolean;       // require ANY of `need` instead of ALL
  disabled?: boolean;  // additional disable reason from caller
  message?: string;    // tooltip when scope missing (default 「您的角色無此權限」)
  children: React.ReactElement;
}

export function ScopeGate(props: ScopeGateProps): React.ReactElement {
  const list = Array.isArray(props.need) ? props.need : [props.need];
  const ok = props.any
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ? useAnyScope(...list)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    : useScope(...list);
  const blocked = !ok;
  const disabled = props.disabled || blocked;
  const child = props.children;
  const title = blocked ? (props.message ?? "您的角色無此權限") : (child.props.title ?? "");
  return React.cloneElement(child, {
    disabled,
    "aria-disabled": disabled || undefined,
    title,
  });
}
